/*
 *
 *  * Copyright (c) [hoarfroster-frontend] 2026. All Rights Reserved.
 *  *
 *  * Last Modified on May 22, 2026 by hoarfroster
 *
 */

import { useRef, useState, KeyboardEvent } from 'react';

export default function GoodPinCode({
    label,
    name,
    defaultValue,
    required = false,
    disabled = false,
}: {
    label: string;
    name: string;
    defaultValue?: number;
    placeholder?: string;
    required?: boolean;
    disabled?: boolean;
}) {
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]),
        [focusing, setFocusing] = useState(-1);

    const handleInputChange = (index: number) => {
        const currentInput = inputRefs.current[index];
        if (currentInput) {
            // restrict length to 1
            if (currentInput.value.length > 1) {
                currentInput.value = currentInput.value.slice(0, 1);
            }
            // focus next input
            if (currentInput.value.length === 1) {
                const next = inputRefs.current[index + 1];
                if (next) {
                    next.focus();
                }
            }
        }
    };

    const handleInputKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
        const currentInput = inputRefs.current[index];
        if (currentInput) {
            if (!['Backspace', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                return;
            }
            // backspace
            if (e.key === 'Backspace' && currentInput.value.length === 0) {
                const previous = inputRefs.current[index - 1];
                if (previous) {
                    previous.focus();
                }
                return;
            }
            // left arrow
            if (e.key === 'ArrowLeft' && currentInput.selectionStart === 0) {
                const previous = inputRefs.current[index - 1];
                if (previous) {
                    previous.focus();
                }
                return;
            }
            // right arrow
            if (e.key === 'ArrowRight' && currentInput.selectionEnd === currentInput.value.length) {
                const next = inputRefs.current[index + 1];
                if (next) {
                    next.focus();
                }
            }
        }
    };

    return (
        <div>
            <label className="text-theme-text-light block text-sm font-bold" htmlFor={name}>
                {label}
            </label>
            <div className="flex justify-between">
                {[0, 1, 2, 3, 4, 5].map((index) => {
                    return (
                        <input
                            key={index}
                            className={`border-theme-border bg-theme-content-bg border outline-none sm:text-sm ${focusing === index ? 'border-primary-500 ring-primary-500 bg-theme-hover ring-[1.25px]' : ''} h-10 w-10 rounded text-center md:h-12 md:w-12`}
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            autoCorrect="off"
                            autoComplete="off"
                            name={name + '_' + index}
                            defaultValue={defaultValue}
                            ref={(el) => {
                                inputRefs.current[index] = el;
                            }}
                            onChange={() => handleInputChange(index)}
                            onKeyDown={(e) => handleInputKeyDown(index, e)}
                            required={required}
                            disabled={disabled}
                            onFocus={() => setFocusing(index)}
                            onBlur={() => setFocusing(-1)}
                        />
                    );
                })}
            </div>
        </div>
    );
}
